import { mapState } from 'pinia';
import stores from 'store/modules';
import moment from 'moment';
import { Device } from '@twilio/voice-sdk';
import Contact from '../store/models/Contact';
import { JSONParse } from 'utils/JSONParse';

Device.prototype.muteIncomingNotification = function (shouldMute = true) {
    try {
        if (shouldMute) {
            this._soundcache.get(Device.SoundName.Incoming).stop();
        } else {
            this._soundcache.get(Device.SoundName.Incoming).play();
        }
    } catch (e) {
        console.error('muteIncomingNotification:', e.message);
    }
};

export default {
    data() {
        return {
            intervalTime: null,
            devicesAudioInput: [],
            devicesAudioOutInput: [],
            devicesAudioAll: [],
            audioInput: null,
            audioOutInput: null,
            streams: [],
            broadcastChannel: new BroadcastChannel('voice-or-video'),
            hasActiveCall: false,
            bcV1CallBroadcast: new BroadcastChannel('v1-call-broadcast'),
            bcV1EventSent: false,
            enumerateDevicesAudioOutput: [],
            enumerateDevicesAudioInput: [],
            enumerateDevicesAudioAll: [],
            testAudio: new Audio(),
            testAudioUrl: '/sounds/notification_call_queue.mp3',
        };
    },

    computed: {
        inboxId() {
            return this.conversation ? this.conversation.inbox_id : this.inbox.id;
        },

        phoneNumber() {
            return this.inboxesArray[0].number.number.slice(2);
        },

        formattedTime() {
            return moment(this.timeTwilio).format('m:ss');
        },

        showCall() {
            return Boolean(this.contactFromCallWidget && this.showCallWidget);
        },

        inboxOwner() {
            return this.inboxesAll.find((item) => item.owner && item.owner.id === this.user.id);
        },

        inboxesAll() {
            return this.inboxesArray;
        },

        contactFrom() {
            let inbox = this.inboxesAll.find((i) => i.id === this.inboxId);
            if (!inbox || !this.conversation) {
                return inbox;
            }

            let number = inbox.numbers.find((n) => n.id === this.conversation.number_id);

            if (number) {
                this.$store.dispatch('handleInboxUpdateProps', {
                    id: inbox.id,
                    props: {
                        number: number,
                    },
                });
            }

            return inbox;
        },

        hasIncoming() {
            return this.directionStatusTwilio === 'INCOMING';
        },

        hasOutgoing() {
            return this.directionStatusTwilio === 'OUTGOING';
        },

        hasConnectConnecting() {
            return this.connectStatusTwilio === 'connecting';
        },

        hasConnectOpen() {
            return this.connectStatusTwilio === 'open';
        },

        hasAcceptVoice() {
            if (this.directionStatusTwilio === 'INCOMING') {
                return this.connectStatusTwilio === 'open';
            }

            return this.directionStatusTwilio === 'OUTGOING';
        },

        getSettingsCookie() {
            return JSON.parse(this.$cookie.get('settings-voice'));
        },

        ...mapState(stores.Organization, ['featureFlags']),
        ...mapState(stores.User, ['user', 'isRecordAutomatically']),
        ...mapState(stores.Inboxes, ['inbox', 'inboxes', 'inboxesArray']),
        ...mapState(stores.Number, ['number']),
        ...mapState(stores.VoiceOrVideo, [
            'showCallWidget',
            'contactFromCallWidget',
            'contactToCallWidget',
            'timeTwilio',
            'twilioRecord',
            'twilioToken',
            'directionStatusTwilio',
            'connectStatusTwilio',
            'twilioSetupRecord',
            'loadingTwilio',
            'twilioDevices',
            'twilioDevicesInput',
            'twilioDevicesOutInput',
            'activeIncomingMute',
        ]),
        ...mapState(stores.Conversations, ['conversation']),
    },

    watch: {
        contactFromCallWidget(data) {
            if (data) {
                this.bcV1CallBroadcast.postMessage({ message: 'call_start' });
            }
        },
    },

    methods: {
        async initContact(numberTo) {
            const number = numberTo || this.numberData.phone_number;
            const contacts = await this.$store.dispatch('searchContacts', {
                query: number,
                update: false,
            });

            if (contacts.length) {
                this.contactTo = contacts[0];
            } else {
                try {
                    this.contactTo = await this.$store.dispatch('createContact', {
                        useAxios: true,
                        data: {
                            number: number,
                        },
                    });
                } catch (e) {
                    this.contactTo = {
                        name: 'Unknown number',
                        number: number,
                        formatted_number: number,
                        photo_url: `${window.location.hostname}/images/app/default-avatar.png`,
                    };
                }
            }

            this.$store.dispatch('initTwilioCallWidget', {
                contactFrom: this.contactFrom,
                contactTo: this.contactTo,
            });
        },

        async setupTwilioCheckMedia() {
            try {
                if (window.device) return;

                this.$store.dispatch('updateStateVoiceTwilio', ['loadingTwilio', true]);
                this.$store.dispatch('updateSettingsVoiceTwilio', [
                    'record',
                    this.isRecordAutomatically,
                ]);

                const stream = await window.navigator.mediaDevices.getUserMedia({ audio: true });

                await this.handleAddStreams(stream);
                await this.stopStreamInputTrack(stream);
                await this.setupTwilio();
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'setupTwilioCheckMedia');
            } finally {
                this.$store.dispatch('updateStateVoiceTwilio', ['loadingTwilio', false]);
            }
        },

        async setupTwilio(numberFrom) {
            try {
                await this.$store.dispatch('createVoiceToken', numberFrom || this.phoneNumber);

                this.destroyDevice();

                window.device = new Device(this.twilioToken, {
                    tokenRefreshMs: 60000 * 30,
                    logLevel: 3,
                });

                // Mechanism to enable and disable sounds in device
                // window.device.audio.incoming(false);
                // window.device.audio.outgoing(false);
                // window.device.audio.disconnect(false);
                await this.eventsTwilioDevice();

                await window.device.register();
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'setupTwilio');
            }
        },

        destroyDevice() {
            if (window.device) {
                window.device.destroy();
            }
        },

        async connectTwilio() {
            const connect = await window.device.connect({
                params: {
                    To: this.conversation?.contact?.number,
                    ConversationId: this.conversation.id,
                },
            });

            this.connectUpdate(connect);
            this.eventsTwilioConnect();
        },

        connectUpdate(connect) {
            window.connect = connect;
            this.$store.dispatch('updateStateVoiceTwilio', [
                'directionStatus',
                window.connect ? window.connect.direction : null,
            ]);
            this.$store.dispatch('updateStateVoiceTwilio', [
                'connectStatus',
                window.connect ? window.connect.status() : null,
            ]);
        },

        deviceUpdate(device) {
            window.device = device;
        },

        async initDevices(startInput = false) {
            if (window.device.state !== 'registered') return;

            this.devicesAudioInput = [];
            this.devicesAudioOutInput = [];
            this.devicesAudioAll = [];

            for (let item of window.device.audio.availableInputDevices.values()) {
                this.devicesAudioInput.push(item);
            }

            for (let item of window.device.audio.availableOutputDevices.values()) {
                this.devicesAudioOutInput.push(item);
            }

            this.devicesAudioAll = [...this.devicesAudioInput, ...this.devicesAudioOutInput];

            this.$store.dispatch('updateSettingsVoiceTwilio', ['devices', this.devicesAudioAll]);
            this.$store.dispatch('updateSettingsVoiceTwilio', [
                'devicesInput',
                this.devicesAudioInput,
            ]);
            this.$store.dispatch('updateSettingsVoiceTwilio', [
                'devicesOutInput',
                this.devicesAudioOutInput,
            ]);

            if (this.devicesAudioInput.length) {
                const audioInput = this.devicesAudioInput[0].deviceId;

                this.$store.dispatch('updateSettingsVoiceTwilio', ['audioInput', audioInput]);
                this.audioInput = audioInput;

                if (startInput) {
                    await this.startInputDevice(this.audioInput);
                }
            }

            if (this.devicesAudioOutInput.length) {
                const audioOutInput = this.devicesAudioOutInput[0].deviceId;

                this.$store.dispatch('updateSettingsVoiceTwilio', ['audioOutInput', audioOutInput]);
                this.audioOutInput = audioOutInput;

                await window.device.audio.speakerDevices.set(this.audioOutInput);
                await window.device.audio.ringtoneDevices.set(this.audioOutInput);
            }
        },

        disconnectTwilio() {
            if (this.connectStatusTwilio === 'pending') {
                window.connect.reject();
            } else {
                window.device.disconnectAll();
            }

            this.connectUpdate(window.connect);
            this.hideCall();
        },

        acceptTwilio() {
            window.connect.accept();
            this.connectUpdate(window.connect);
        },

        async hideCall() {
            this.bcV1CallBroadcast.postMessage({ message: 'call_end' });
            this.bcV1EventSent = false;
            this.$store.dispatch('initTwilioCallWidget', {
                contactFrom: null,
                contactTo: null,
            });
            await this.$store.dispatch('showTwilioCallWidget', false);
            await this.$store.dispatch('incrementTimeTwilio', true);
            await this.$store.dispatch('updateSettingsVoiceTwilio', ['setupRecord', false]);
        },

        errorCodeAction(error) {
            if (error.code === 31205) {
                this.$store.dispatch('updateStateVoiceTwilio', ['loadingTwilio', true]);

                this.setupTwilio();

                return;
            }

            if (error.code === 0) {
                this.$store.dispatch('showToast', {
                    type: 'danger',
                    message:
                        'To enable calling, please allow Salesmsg to access your microphone. <a target="_blank" href="https://help.salesmessage.com/en/articles/5463800-inbound-outbound-calling-with-salesmsg#h_313708a719">Learn more</a>',
                });

                return;
            }

            this.$store.dispatch('showToast', {
                type: 'danger',
                message: `${error.message} <a target="_blank" href="https://help.salesmessage.com/en/articles/5463800-inbound-outbound-calling-with-salesmsg#h_313708a719">Learn more</a>`,
            });
        },

        eventsTwilioDevice() {
            window.device.on('error', this.handleEventError);
            window.device.on('incoming', this.handleEventIncoming);
            window.device.on('registered', this.handleEventRegistered);
            window.device.on('registering', this.handleEventRegistering);
            window.device.on('unregistered', this.handleEventUnregistered);
            window.device.on('tokenWillExpire', this.handleEventTokenWillExpire);
            window.device.audio.on('deviceChange', this.handleEventAudioDeviceChange);
        },

        eventsTwilioConnect() {
            window.connect.on('accept', this.handleEventConnectAccept);
            window.connect.on('cancel', this.handleEventConnectCancel);
            window.connect.on('disconnect', this.handleEventConnectDisconnect);
            window.connect.on('error', this.handleEventConnectError);
            window.connect.on('mute', this.handleEventConnectMute);
            window.connect.on('reconnected', this.handleEventConnectReconnected);
            window.connect.on('reject', this.handleEventConnectReject);
            window.connect.on('ringing', this.handleEventConnectRinging);
            window.connect.on('sample', this.handleEventConnectSample);
            window.connect.on('warning', (warningName, warningData) => {
                const logData = {
                    warning: warningName,
                    details: warningData,
                };

                const qualityLogData = {
                    callSid: window.connect.parameters.CallSid,
                    logData: logData,
                };

                this.$store.dispatch('sendCallQualityLog', qualityLogData);
            });
        },

        // Connect Events
        handleEventConnectRinging(hasEarlyMedia) {},

        handleEventConnectReconnected(connect) {},

        async handleEventConnectDisconnect(connect) {
            await this.connectUpdate(connect);
            await this.incrementTime(true);
            await this.hideCall();
            await this.stopInputDevice(this.audioInput);
        },

        handleEventConnectError(error) {
            console.log('error', error);
        },

        async handleEventConnectAccept(connect) {
            this.broadcastChannel.postMessage({ message: 'incoming' });

            await this.connectUpdate(connect);
            await this.incrementTime();
        },

        handleEventConnectSample(e) {},

        handleEventConnectReject() {
            this.incrementTime(true);
            this.hideCall();
        },

        handleEventConnectMute(status, connect) {
            this.connectUpdate(connect);
        },

        handleEventConnectCancel(connect) {
            this.connectUpdate(connect);
            this.incrementTime(true);
            this.hideCall();
        },

        // Device Audio Events
        handleEventAudioDeviceChange(lostActiveDevices, id) {
            this.initDevices();
        },

        async handleEventRegistering(device) {},

        async handleInitRecord(type = 'init', statusRecord) {
            if (type === 'init') {
                if (this.twilioRecord) {
                    await this.$store.dispatch('updateVoiceRecordStatus', {
                        callSid: window.connect.parameters.CallSid,
                        record: 'start',
                    });
                    await this.$store.dispatch('updateSettingsVoiceTwilio', ['setupRecord', true]);
                    await this.$store.dispatch('updateVoiceRecordStatus', {
                        callSid: window.connect.parameters.CallSid,
                        record: this.twilioRecord,
                    });
                }
            }

            if (type === 'toggle') {
                if (!this.twilioSetupRecord) {
                    await this.$store.dispatch('updateVoiceRecordStatus', {
                        callSid: window.connect.parameters.CallSid,
                        record: 'start',
                    });
                    await this.$store.dispatch('updateSettingsVoiceTwilio', ['setupRecord', true]);
                }

                await this.$store.dispatch('updateVoiceRecordStatus', {
                    callSid: window.connect.parameters.CallSid,
                    record: statusRecord,
                });
            }
        },

        async handleEventError(error) {
            // Access token expired error code
            if (error.code === 20104) {
                await this.handleEventTokenWillExpire();
            } else {
                console.log('An error has occurred: ', error);
            }
        },

        async handleEventRegistered() {
            await this.initDevices();
            await this.stopInputDevice(this.audioInput);
            await this.$store.dispatch('updateStateVoiceTwilio', ['loadingTwilio', false]);
        },

        async handleEventIncoming(connect) {
            let contactResponse = JSONParse(
                connect.customParameters.get('contact'),
                'handleEventIncoming ParseContact'
            );
            let inboxResponse = JSONParse(
                connect.customParameters.get('inbox'),
                'handleEventIncoming ParseInbox'
            );
            const contact = contactResponse
                ? new Contact(contactResponse)
                : this.conversation.contact;
            const inbox = inboxResponse ? this.inboxes[inboxResponse.id] : this.contactFrom;

            this.$store.dispatch('updateStateVoiceTwilio', ['activeIncomingMute', inbox.isMuted]);
            window.device.muteIncomingNotification(inbox.isMuted || this.hasActiveCall);

            await this.connectUpdate(connect);
            this.eventsTwilioConnect();

            this.bcV1CallBroadcast.postMessage({ message: 'call_start' });
            this.bcV1EventSent = true;

            await this.$store.dispatch('initTwilioCallWidget', {
                contactFrom: inbox,
                contactTo: contact,
            });
            await this.$store.dispatch('showTwilioCallWidget', true);
            await this.$store.dispatch('incrementTimeTwilio', true);

            return {
                inbox: inbox,
                contact: contact,
            };
        },

        handleEventUnregistered() {},

        async handleEventTokenWillExpire(expired = false) {
            this.$store.dispatch('updateStateVoiceTwilio', ['loadingTwilio', true]);
            if (!expired) {
                this.$log.info(
                    'Twilio token will expire in 10 seconds',
                    this.$options.__file,
                    'tokenWillExpire'
                );
            } else {
                this.$log.info('Twilio token expired', this.$options.__file, 'tokenWillExpire');
            }
            await this.$store.dispatch('createVoiceToken');
            this.$log.info(
                'New token is: ' + this.twilioToken,
                this.$options.__file,
                'tokenWillExpire'
            );
            window.device.updateToken(this.twilioToken);
            this.$store.dispatch('updateStateVoiceTwilio', ['loadingTwilio', false]);
        },

        async startInputDevice(deviceId) {
            try {
                await window.device?.audio.setInputDevice(deviceId);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'startInputDevice');
            }
        },

        async stopInputDevice(deviceId) {
            try {
                await window.device?.audio.unsetInputDevice(deviceId);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'stopInputDevice');
            }
        },

        stopStreamInputTrack(stream) {
            if (!stream) {
                setTimeout(() => {
                    this.stopStreamInputTrack(stream);
                }, 100);
            } else {
                stream.getAudioTracks().forEach((track) => {
                    track.stop();
                });
            }
        },

        incrementTime(reset = false) {
            clearInterval(this.intervalTime, reset);

            if (!reset) {
                this.intervalTime = setInterval(() => {
                    this.$store.dispatch('incrementTimeTwilio');
                }, 1000);
            } else {
                this.$store.dispatch('incrementTimeTwilio', reset);
            }
        },

        handleAddStreams(stream) {
            this.streams.push(stream);
        },

        handleStopStreams() {
            this.streams.forEach((stream) => {
                this.stopStreamInputTrack(stream);
            });

            this.streams = [];
        },

        async getDevices() {
            try {
                const enumerateDevices = await window.navigator.mediaDevices.enumerateDevices();
                this.enumerateDevicesAudioOutput = enumerateDevices.filter(
                    (item) => item.kind === 'audiooutput'
                );
                this.enumerateDevicesAudioInput = enumerateDevices.filter(
                    (item) => item.kind === 'audioinput'
                );

                this.enumerateDevicesAudioAll = [
                    ...this.enumerateDevicesAudioOutput,
                    ...this.enumerateDevicesAudioInput,
                ];

                this.$store.dispatch('updateSettingsVoice', [
                    'devices',
                    this.enumerateDevicesAudioAll,
                ]);
                this.$store.dispatch('updateSettingsVoice', [
                    'devicesInput',
                    this.enumerateDevicesAudioInput,
                ]);
                this.$store.dispatch('updateSettingsVoice', [
                    'devicesOutInput',
                    this.enumerateDevicesAudioOutput,
                ]);

                if (this.enumerateDevicesAudioInput.length) {
                    const audioInput = this.enumerateDevicesAudioInput[0].deviceId;

                    this.$store.dispatch('updateSettingsVoice', ['audioInput', audioInput]);
                }

                if (this.enumerateDevicesAudioOutput.length) {
                    const audioOutInput = this.enumerateDevicesAudioOutput[0].deviceId;

                    this.$store.dispatch('updateSettingsVoice', ['audioOutInput', audioOutInput]);
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'getDevices');
            }
        },
    },

    mounted() {
        this.broadcastChannel.onmessage = ({ data }) => {
            if (data?.message === 'incoming') {
                this.hasActiveCall = true;
            }

            if (data?.message === 'call_end') {
                this.hasActiveCall = false;
            }
        };
    },

    unmounted() {
        if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({ message: 'call_end' });
            this.broadcastChannel.close();
        }

        if (this.bcV1EventSent) {
            this.bcV1CallBroadcast.postMessage({ message: 'call_end' });
        }
        this.bcV1CallBroadcast.close();
    },
};
